import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { forkJoin, of, catchError, switchMap } from 'rxjs';

import { BrandThemesEnum, SdSpyTargetDirective, SizesEnum } from '@seb/web-design-system/core';
import { IRecipeCard } from '@seb/web-design-system/recipe-card';

import { BaseRecipeListComponent } from '../../../components/base/base-recipe-list/base-recipe-list.component';
import { Recipe, recipeCardSkeleton, RecipeListComponentInterface } from '../../../models';
import { AbstractComponent } from '../abstract-component.component';
import { WrapperComponent } from '../../../components/static/core';
import { RecipeService } from '../../../services';

@Component({
  selector: 'wc-recipe-list',
  templateUrl: './recipe-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdSpyTargetDirective,
    WrapperComponent,
    BaseRecipeListComponent
  ]
})
export class RecipeListComponent extends AbstractComponent<RecipeListComponentInterface> implements OnInit {
  public headingTitleThemesConfig = BrandThemesEnum.brand;
  public recipes: IRecipeCard[];
  public sizesEnum = SizesEnum;
  public recipesSize: number;

  constructor(
    private recipeService: RecipeService,
    private cdRef: ChangeDetectorRef
  ) {
    super();

    if (this.useLazyLoad) {
      this.recipes = new Array(3).fill(undefined).map(
        (_, i) => ({ ...recipeCardSkeleton, id: `${i}` })
      );
    }
  }

  override mandatoryFunction = (cmp: RecipeListComponentInterface): boolean => {
    return !!cmp.recipeList;
  };

  ngOnInit(): void {
    this.subscriptions.add(
      this.component$.pipe(
        switchMap((cmp: RecipeListComponentInterface) => forkJoin(
          cmp.recipeList.split(',').map((recipeId: string) => {
            // Extract recipe source system (recipe id format: 'DCP_SOURCE_SYSTEM/DCP_ID')
            let sourceSystem: string;
            let id: string;
            const idParts: string[] = recipeId.split(/\/(.+)/);
            if (idParts.length === 3) {
              // Correct form
              if (recipeId.startsWith('/v1/variants/')) {
                // (recipe id format '/v1/variants/id/')
                sourceSystem = 'PRO';
                id = recipeId.endsWith('/') ? recipeId : recipeId + '/'; // check if the recipeid ends with /
              } else {
                sourceSystem = idParts[0];
                id = idParts[1];
              }
            } else {
              sourceSystem = 'PRO'; // => best effort: force PRO value, as most recipes are on PRO
              id = recipeId;
            }
            return this.recipeService.getRecipe(sourceSystem, id).pipe(
              catchError(() => of(null)),
              switchMap((recipe: Recipe | null) => (recipe ? this.recipeService.extractRecipe(recipe) : of(null)))
            );
          })
        ))
      ).subscribe((results) => {
        this.recipes = results.filter((recipe) => recipe !== null);
        this.recipesSize = this.recipes?.length > 0 ? this.recipes?.length : 0;

        this.setLoaded();
        this.cdRef.markForCheck();
      })
    );
  }
}
